import { useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import {
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSPanelHeader,
  MDSTextVariant,
  MDSLensGroup,
  MDSIconButton,
  MDSPanelHeaderButtonGroup,
  MDSTextFieldSlotName,
  MDSIconButtonSize,
} from "@/mds";
import { PageBannerSection } from "@/components/banners/page-banner-section";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { useCallback } from "react";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { CollectionsFacetFilter } from "@/components/filters/CollectionsFacetFilter/CollectionsFacetFilter";
import { ContactsFacetFilter } from "@/components/filters/ContactsFacetFilter";
import { FilterField } from "@/components/filters/FilterField";
import { SortDropdownButton } from "@/components/filters/SortDropdownButton";

export const TemplatesListPageHeader = observer(() => {
  const page = useTabPageStore("templatesListPage");
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const onTemplatesFilterChange = useCallback(
    (value: string) => {
      page.setTemplatesFilterQuery({ query: value });
    },
    [page]
  );

  const clearTemplatesFilterQuery = useCallback(() => {
    page.setTemplatesFilterQuery({ query: "" });
  }, [page]);

  const tabs = [
    {
      label: "All",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.All,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.All,
    },
    {
      label: "Created by me",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.AddedByMe,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.AddedByMe,
    },
    {
      label: "Shared with me",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.SharedWithMe,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.SharedWithMe,
    },
  ];

  return (
    <Wrapper>
      <MDSPanelHeader>
        <MDSText
          slot={MDSPanelHeaderSlotName.Heading}
          variant={MDSTextVariant.HeaderXl}
          numberOfLines={1}
        >
          Templates
        </MDSText>
        <MDSLensGroup slot={MDSPanelHeaderSlotName.Subsection} items={tabs} />
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSTooltip config={{ label: "Create template", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              iconKind={MdsIconKind.Plus}
              onClick={page.handleCreateNewTemplate}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
          <MDSTooltip config={{ label: "Show filters", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              iconKind={MdsIconKind.Filter}
              onClick={() => setShowFilters(!showFilters)}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>

      {showFilters && (
        <WrapperBottom>
          <FiltersContainer>
            <FilterField
              placeholder="Filter by name"
              value={page.templatesFilterQuery}
              onChange={onTemplatesFilterChange}
              onClearText={clearTemplatesFilterQuery}
              showClearTextButton={true}
              autoFocus={true}
            >
              <MdsIcon kind={MdsIconKind.Search} slot={MDSTextFieldSlotName.LeftAddon} />
            </FilterField>
            <SortDropdownButton
              isToggled={page.filters.sortBy !== SortByKind.LastModified}
              contentList={page.filters.sortOptions}
              label={page.filters.sortLabel}
              placement="below-right-alignment"
              dropdownIcon
            />
            {renderSortedFilters(page.filters)}
          </FiltersContainer>
        </WrapperBottom>
      )}
      <PageBannerSection />
    </Wrapper>
  );
});

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          supportNoCollectionOption
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing.md,
}));

const FiltersContainer = styled(FadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing.sm,
}));
